import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, documentId, getDocFromServer, getDocsFromServer, orderBy, query, where, limit, startAfter } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { readSalesReportCache, writeSalesReportCache } from '../lib/salesReportCache';

const inFlight = new Map();
const CACHE_EPOCH = 'sales-report-api-v1';
const toDate = (value) => {
  if (value?.toDate) return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1e6);
  return new Date(value);
};
const isoDay = (value) => {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const normalizeSale = (id, value) => {
  const serializeDate = (date) => date?.toDate ? date.toDate().toISOString() : date;
  return {
    ...value, id,
    dateKey: value.fecha ?? value.dateKey ?? isoDay(value.date),
    fecha: value.fecha ?? value.dateKey ?? isoDay(value.date),
    date: serializeDate(value.date),
    updatedAt: serializeDate(value.updatedAt || value.createdAt || value.date),
  };
};
const monthsForRange = (start, end) => {
  const months = [];
  let [year, month] = start.slice(0, 7).split('-').map(Number);
  const last = end.slice(0, 7);
  while (`${year}-${String(month).padStart(2, '0')}` <= last) {
    months.push(`${year}-${String(month).padStart(2, '0')}`);
    month += 1;
    if (month > 12) { month = 1; year += 1; }
  }
  return months;
};

async function readRevisions(months) {
  if (months === null) {
    const snapshot = await getDocsFromServer(collection(db, 'salesReportChanges'));
    return Object.fromEntries(snapshot.docs.map(entry => {
      const revision = entry.data().revision;
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(entry.id) || !Number.isSafeInteger(revision) || revision < 0) throw new Error('El registro de cambios no es válido.');
      return [entry.id, revision];
    }));
  }
  const snapshots = await Promise.all(months.map(month => getDocFromServer(doc(db, 'salesReportChanges', month))));
  return Object.fromEntries(snapshots.map((snapshot, index) => {
    const revision = snapshot.exists() ? snapshot.data().revision : 0;
    if (!Number.isSafeInteger(revision) || revision < 0) throw new Error('La versión del reporte no es válida.');
    return [months[index], revision];
  }));
}

async function initialSales(startYMD, endYMD, pageSize, allHistory) {
  const rows = [];
  let cursor = null;
  while (true) {
    const constraints = allHistory ? [orderBy(documentId(), 'asc'), limit(pageSize)] : [where('fecha', '>=', startYMD), where('fecha', '<=', endYMD), orderBy('fecha', 'desc'), limit(pageSize)];
    if (cursor) constraints.push(startAfter(cursor));
    const snapshot = await getDocsFromServer(query(collection(db, 'ventas'), ...constraints));
    rows.push(...snapshot.docs.map(snap => normalizeSale(snap.id, snap.data())));
    if (snapshot.size < pageSize) return rows;
    const next = snapshot.docs[snapshot.docs.length - 1];
    if (!next || next.id === cursor?.id) throw new Error('No se pudo completar la paginación de ventas.');
    cursor = next;
  }
}

async function applyChanges(rows, versions, from, to, startYMD, endYMD, pageSize, allHistory) {
  if (Object.keys(from).some(month => !Object.prototype.hasOwnProperty.call(to, month))) throw new Error('El registro de cambios perdió continuidad.');
  const merged = new Map(rows.map(row => [row.id, row]));
  const nextVersions = { ...versions };
  for (const month of Object.keys(to)) {
    let revision = from[month] ?? 0;
    if (!Number.isSafeInteger(revision) || revision > to[month]) throw new Error('El registro de cambios perdió continuidad.');
    while (revision < to[month]) {
      const snapshot = await getDocsFromServer(query(collection(db, 'salesReportChanges', month, 'entries'),
        where('revision', '>', revision), where('revision', '<=', to[month]), orderBy('revision', 'asc'), limit(pageSize)));
      if (!snapshot.size) throw new Error('Faltan cambios de ventas por sincronizar.');
      for (const entry of snapshot.docs) {
        const change = entry.data();
        if (change.revision !== revision + 1 || !change.saleId) throw new Error('El registro de cambios está incompleto.');
        revision = change.revision;
        if (!Number.isSafeInteger(change.saleVersion) || change.saleVersion <= 0) throw new Error('La versión del cambio no es válida.');
        if (change.saleVersion <= (nextVersions[change.saleId] || 0)) continue;
        nextVersions[change.saleId] = change.saleVersion;
        const sale = change.sale ? normalizeSale(change.saleId, change.sale) : null;
        if (sale && !sale.deleted && (allHistory || (sale.dateKey >= startYMD && sale.dateKey <= endYMD))) merged.set(sale.id, sale);
        else merged.delete(change.saleId);
      }
    }
  }
  return { data: [...merged.values()], versions: nextVersions };
}

async function loadSales({ cacheKey, startYMD, endYMD, pageSize, allHistory }) {
  if (inFlight.has(cacheKey)) return inFlight.get(cacheKey);
  const request = (async () => {
    const months = allHistory ? null : monthsForRange(startYMD, endYMD);
    const cached = await readSalesReportCache(cacheKey);
    let revisions = await readRevisions(months);
    let data;
    let versions = {};
    if (cached?.epoch === CACHE_EPOCH && Array.isArray(cached.data) && cached.revisions) {
      ({ data, versions } = await applyChanges(cached.data, cached.versions, cached.revisions, revisions, startYMD, endYMD, pageSize, allHistory));
    } else {
      // Baseline captured BEFORE downloading: concurrent changes cannot be skipped.
      data = (await initialSales(startYMD, endYMD, pageSize, allHistory)).filter(sale => !sale.deleted);
      const latest = await readRevisions(months);
      ({ data, versions } = await applyChanges(data, versions, revisions, latest, startYMD, endYMD, pageSize, allHistory));
      revisions = latest;
    }
    data.sort((a, b) => (toDate(b.date).getTime() || 0) - (toDate(a.date).getTime() || 0));
    const payload = { epoch: CACHE_EPOCH, data, revisions, versions };
    const cachePersistent = await writeSalesReportCache(cacheKey, payload);
    return { ...payload, cachePersistent };
  })();
  inFlight.set(cacheKey, request);
  try { return await request; } finally { inFlight.delete(cacheKey); }
}

export function useSalesData({ startDate, endDate, max = 500, allHistory = false } = {}) {
  const pageSize = Math.max(1, Math.min(Number(max) || 500, 1000));
  const startYMD = startDate || endDate || '';
  const endYMD = endDate || startDate || '';
  const userId = auth?.currentUser?.uid || 'anonymous';
  const cacheKey = useMemo(() => `salesReport:v4:${userId}:${allHistory ? 'all' : `${startYMD}:${endYMD}`}`, [userId, startYMD, endYMD, allHistory]);
  const [state, setState] = useState({ key: null, salesData: [], loading: true, error: null, truncated: false, cachePersistent: true });
  const generation = useRef(0);
  const fetchSales = useCallback(async () => {
    const current = ++generation.current;
    setState({ key: cacheKey, salesData: [], loading: true, error: null, truncated: false, cachePersistent: true });
    try {
      if (!allHistory && (!/^\d{4}-(0[1-9]|1[0-2])-\d{2}$/.test(startYMD) || !/^\d{4}-(0[1-9]|1[0-2])-\d{2}$/.test(endYMD) || startYMD > endYMD)) {
        throw new Error('Selecciona un rango de fechas válido.');
      }
      const result = await loadSales({ cacheKey, startYMD, endYMD, pageSize, allHistory });
      if (generation.current === current) setState({ key: cacheKey, salesData: result.data, loading: false, error: null, truncated: false, cachePersistent: result.cachePersistent });
    } catch (error) {
      if (generation.current === current) setState({ key: cacheKey, salesData: [], loading: false, error, truncated: false, cachePersistent: true });
    }
  }, [cacheKey, startYMD, endYMD, pageSize, allHistory]);
  useEffect(() => {
    fetchSales();
    return () => { generation.current += 1; };
  }, [fetchSales]);
  if (state.key !== cacheKey) return { salesData: [], loading: true, error: null, truncated: false, cachePersistent: true, reload: fetchSales };
  return { ...state, reload: fetchSales };
}
